import { CommonUtil } from '../utils/commonUtil';
import _ from 'lodash';
import dayjs from 'dayjs';

/**
 * 성능지 보험증서 콜센터 전화번호
 * - 우선순위: shop > enterprise > insurance > default
 * - side: 'front' | 'rear'
 *
 * 보험사코드: 08 삼성화재, 10 KB, 13 DB, 69 캐롯
 * 업체: 32 모빌리티, 39 카진
 * 성능장: 123 수원성능장(C42), 124 광주풍암2(J51)
 *
 * 모빌리티365(enterpriseId: 32, code: R99) + 삼성화재(08) 보험가입확인서 뒷장은
 * GUIDE_MOBILITY365_SAMSUNG_REAR.jpg 를 사용하며, 이미지에 보험처리상담(1668-4774)과
 * 삼성화재콜센터(1588-5114)가 함께 포함되어 있어 rear 오버레이를 하지 않는다.
 */
export const CALL_CENTER_PHONE = {
  DEFAULT: {
    front: '',
    rear: '1600-3528',
  },
  BY_SHOP: {
    123: { front: '', rear: '1660-1396' }, // 수원성능장 (C42)
    124: { front: '', rear: '1660-1396' }, // 광주풍암2 (J51)
  },
  BY_ENTERPRISE: {
    32: { front: '', rear: '1668-4774' }, // 모빌리티 (삼성화재 08 뒷장은 전용 이미지 사용)
    39: { front: '1811-8769', rear: '' }, // 카진
  },
  BY_INSURANCE: {
    '08': { front: '', rear: '1660-1396' }, // 삼성화재
    13: { front: '1600-3528', rear: '1600-3528' }, // DB
    69: { front: '', rear: '' }, // 캐롯
  },
};

/**
 * @param {number|string|null|undefined} enterpriseId
 * @param {string|null|undefined} insuranceCode
 * @param {'front'|'rear'} side
 * @param {number|string|null|undefined} shopId
 * @returns {string}
 */
export function getCallCenterPhone(
  enterpriseId,
  insuranceCode,
  side = 'rear',
  shopId
) {
  const byShop = _.get(CALL_CENTER_PHONE.BY_SHOP, shopId);
  if (byShop) {
    return _.get(byShop, side, '') || '';
  }

  const byEnterprise = _.get(CALL_CENTER_PHONE.BY_ENTERPRISE, enterpriseId);
  if (byEnterprise) {
    return _.get(byEnterprise, side, '') || '';
  }

  const byInsurance = _.get(CALL_CENTER_PHONE.BY_INSURANCE, insuranceCode);
  if (byInsurance) {
    return _.get(byInsurance, side, '') || '';
  }

  return _.get(CALL_CENTER_PHONE.DEFAULT, side, '') || '';
}

const CERTIFICATE_DATAS = [
  {
    name: 'inspectorName',
    objName: 'inspector',
    fieldName: 'enterpriseName',
    title: '점검업체이름',
    insuranceCompanyCode: ['08', '10', '13', '69'], // DB, 캐롯
    pos: {
      // insuranceCode: [top, left]
      '08': [535, 198],
      10: [235, 158],
      13: [323, 230],
      69: [258, 465],
    },
    getValue: (totalValues) => {
      return _.get(totalValues, 'inspector.enterpriseName');
    },
  },
  {
    name: 'shopName',
    objName: 'inspector',
    fieldName: 'shopName',
    title: '점검장이름',
    insuranceCompanyCode: ['10', '13', '69'], // DB
    pos: {
      10: [258, 158],
      13: [323, 470],
      69: [258, 515],
    },
    getValue: (totalValues) => {
      return _.get(totalValues, 'inspector.shopName');
    },
  },
  {
    name: 'VIN',
    objName: 'checkData',
    fieldName: 'VIN',
    title: '차대번호전체',
    insuranceCompanyCode: ['10', '13', '69'], // DB, 캐롯
    pos: {
      10: [380, 485],
      13: [345, 150],
      69: [278, 465],
    },
    getValue: (totalValues) => {
      if (_.get(totalValues, 'checkData.VIN').length === 17) {
        return _.get(totalValues, 'checkData.VIN');
      } else {
        return (
          _.get(totalValues, 'vehicleInformation.VIN') +
          _.get(totalValues, 'vehicleInformation.vinRemain')
        );
      }
    },
  },
  {
    name: 'CHCK_ENTRPS_BIZ_NO',
    objName: 'checkData',
    fieldName: 'CHCK_ENTRPS_BIZ_NO',
    title: '사업자번호',
    insuranceCompanyCode: ['08'], // DB, 캐롯
    pos: {
      '08': [535, 505],
    },
    getValue: (totalValues) => {
      return CommonUtil.Formatter.biz(
        totalValues?.checkData?.CHCK_ENTRPS_BIZ_NO
      );
    },
  },
  {
    name: 'statementNumber',
    objName: 'checkData',
    fieldName: 'PRFOMNC_CHCK_NO',
    title: '성능점검번호',
    insuranceCompanyCode: ['13'], // DB
    pos: {
      13: [345, 400],
    },
    getValue: (totalValues) => {
      return _.get(totalValues, 'checkData.PRFOMNC_CHCK_NO');
    },
  },
  {
    name: 'CNM',
    objName: 'vehicleInformation',
    fieldName: 'CNM',
    title: '차량명',
    insuranceCompanyCode: ['10'], //KB
    pos: {
      10: [405, 158],
    },
    getValue: (totalValues) => {
      return `${_.get(totalValues, 'vehicleInformation.CNM')}${_.get(
        totalValues,
        'vehicleInformation.cnmDetail'
      )}`;
    },
  },
  {
    name: 'CHCK_DE',
    objName: 'checkData',
    fieldName: 'CHCK_DE',
    title: '성능점검일자',
    insuranceCompanyCode: ['10', '13'], // KB, DB
    pos: {
      10: [405, 485],
      13: [345, 610],
    },
    getValue: (totalValues) => {
      return dayjs(_.get(totalValues, 'checkData.CHCK_DE')).format(
        'YYYY-MM-DD'
      );
    },
  },

  {
    name: 'VHRNO',
    objName: 'checkData',
    fieldName: 'VHRNO',
    title: '차량번호',
    insuranceCompanyCode: ['08', '10', '13', '69'], // KB, DB, 캐롯
    pos: {
      '08': [565, 505],
      10: [380, 158],
      13: [365, 150],
      69: [278, 155],
    },
    getValue: (totalValues) => {
      return _.get(totalValues, 'checkData.VHRNO');
    },
  },

  {
    name: 'insurancePolicyNumber',
    objName: 'paymentInformation',
    fieldName: 'insurancePolicyNumber',
    title: '보험증권번호',
    insuranceCompanyCode: ['08', '13'], // DB
    pos: {
      '08': [565, 198],
      13: [365, 400],
    },
    getValue: (totalValues) => {
      return _.get(totalValues, 'paymentInformation.insurancePolicyNumber');
    },
  },

  {
    name: 'paidInsurancePriceAmount',
    objName: 'paymentInformation',
    fieldName: 'paidInsurancePriceAmount',
    title: '보험료',
    insuranceCompanyCode: ['13'], // DB
    pos: {
      13: [365, 610],
    },
    getValue: (totalValues) => {
      // DB 보험의 경우 insurancePrice + insurancePriceVAT 사용
      const insurancePrice = _.get(totalValues, 'paymentInformation.insurancePrice', 0);
      const insurancePriceVAT = _.get(totalValues, 'paymentInformation.insurancePriceVAT', 0);
      const paidAmount = _.get(totalValues, 'paymentInformation.paidInsurancePriceAmount');
      
      // paidInsurancePriceAmount가 있으면 사용, 없으면 insurancePrice + insurancePriceVAT 사용
      if (paidAmount !== undefined && paidAmount !== null && paidAmount !== '') {
        return `${CommonUtil.Unit.format(paidAmount)}원`;
      } else {
        return `${CommonUtil.Unit.format(insurancePrice + insurancePriceVAT)}원`;
      }
    },
  },
  {
    name: 'insuranceType',
    title: '보험종목',
    insuranceCompanyCode: ['69'], // 캐롯
    pos: {
      69: [258, 155],
    },
    getValue: (totalValues) => {
      return '자동차 성능∙상태점검 책임보험';
    },
  },
  {
    name: 'customerServiceCenterNumber',
    title: '상담번호',
    insuranceCompanyCode: ['08', '10', '13'], // 삼성화재, KB, DB
    pos: {
      '08': [],
      10: [],
      13: [],
    },
    getValue: (totalValues) => {
      return getCallCenterPhone(
        null,
        _.get(totalValues, 'paymentInformation.insuranceCompany'),
        'rear',
        _.get(totalValues, 'inspector.shopId')
      );
    },
  },
];

export { CERTIFICATE_DATAS };
